import type { QueryClient } from '@tanstack/react-query';
import { notFound, redirect } from '@tanstack/react-router';
import { getDashboardsQueryOptions } from '@/lib/queries/loop-visualization/useDashboard';
import { getVisualizationViewsQueryOptions } from '@/lib/queries/view/useVisualizationsView';
import {
  getLastVisitedMainVisualization,
  getLastVisitedView,
} from './lastVisitedVisualizationView';

export async function redirectToLastLoopVisualization({
  org,
  queryClient,
  loopId,
}: {
  org: string;
  queryClient: QueryClient;
  loopId: number;
}): Promise<void> {
  if (!loopId) throw notFound();

  const lastVisited = getLastVisitedView(loopId);

  if (lastVisited?.type === 'view') {
    const views = await queryClient.ensureQueryData(getVisualizationViewsQueryOptions({ loopId }));
    if (views.some((v) => v.viewId === lastVisited.id)) {
      throw redirect({
        to: `/org/$org/loop/$loopId/visualizations/view/$viewId`,
        params: { org, loopId, viewId: lastVisited.id },
        replace: true,
      });
    }
  }

  if (lastVisited?.type === 'dashboard') {
    const dashboards = await queryClient.ensureQueryData(getDashboardsQueryOptions(loopId));
    if (dashboards.some((d) => d.id === lastVisited.id)) {
      throw redirect({
        to: `/org/$org/loop/$loopId/visualizations/dashboard/$dashboardId`,
        params: { org, loopId, dashboardId: lastVisited.id },
        replace: true,
      });
    }
  }

  const mainVisualization = getLastVisitedMainVisualization(loopId) ?? 'table';
  throw redirect({
    to: `/org/$org/loop/$loopId/visualizations/main/${mainVisualization}`,
    params: { org, loopId },
    replace: true,
  });
}
